/**
 * HTTP transport.
 *
 * One place that knows how to reach the API, attach credentials, and turn a non-2xx
 * response into a typed error. Nothing else in the app calls `fetch` directly — when
 * auth changes, or a retry policy is added, or a request id header is introduced, this
 * is the only file that moves.
 */

import { storage } from '../lib/storage'

const RAW_BASE = import.meta.env.VITE_API_BASE_URL ?? ''
const BASE_URL = RAW_BASE.replace(/\/+$/, '')
const API_PREFIX = '/api/v1'

/** Exported so tests and the auth provider agree on the key without duplicating it. */
export const TOKEN_KEY = 'lcrm.auth.token'

/** Fired when the API says our credentials are no longer good. `AuthProvider` listens. */
export const UNAUTHORIZED_EVENT = 'lcrm:unauthorized'

/** The one error shape the backend produces — see `app/core/errors.py`. */
interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /**
   * True when the request was fine but the server deliberately said no in a way a user
   * can fix by changing the input (bad field, duplicate code) rather than by retrying.
   */
  get isClientFault(): boolean {
    return this.status >= 400 && this.status < 500
  }
}

export function getToken(): string | null {
  return storage.get(TOKEN_KEY)
}

export function setToken(token: string | null): void {
  if (token) storage.set(TOKEN_KEY, token)
  else storage.remove(TOKEN_KEY)
}

/**
 * Parse a response body defensively.
 *
 * A 502 from the ingress is an HTML page, not our JSON envelope, and `response.json()`
 * would throw a SyntaxError that reads like a frontend bug. Returns null instead, and
 * the caller falls back to a status-based message.
 */
async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function toApiError(response: Response, body: unknown): ApiError {
  const envelope = body as ErrorEnvelope | null
  const code = envelope?.error?.code ?? `http_${response.status}`
  const message =
    envelope?.error?.message ??
    (response.status >= 500
      ? 'The server failed to handle that request.'
      : `Request failed with HTTP ${response.status}.`)
  return new ApiError(response.status, code, message, envelope?.error?.details)
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** Set for calls that must not carry the session (login in particular). */
  anonymous?: boolean
  signal?: AbortSignal
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, signal } = options

  const headers = new Headers({ Accept: 'application/json' })
  if (body !== undefined) headers.set('Content-Type', 'application/json')

  if (!anonymous) {
    const token = getToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
  }

  const response = await fetch(`${BASE_URL}${API_PREFIX}${path}`, {
    method,
    headers,
    signal,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })

  if (response.status === 401 && !anonymous) {
    // Drop the dead session and tell the app. Every caller does not need to handle this.
    setToken(null)
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
  }

  // 204 has no body by definition, and `parseBody` would return null anyway — but being
  // explicit means a DELETE does not need a lie about its return type.
  if (response.status === 204) return undefined as T

  const parsed = await parseBody(response)
  if (!response.ok) throw toApiError(response, parsed)
  return parsed as T
}
